import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './ProjectMilestones.css';
import lpApi from '../../helpers/lpApi';

// =============================================================================
// ProjectMilestones — shared MILESTONE ESCROW view for the Custom Projects
// (Gros Projets) module. Step #5 (the FINAL piece) of the
// client -> company -> accept -> contract -> ESCROW loop. Used by BOTH the
// client (MyProjects) and the company (CompanyProjects), embedded by the shared
// ProjectContract component once a contract is ACTIVE.
//
// All backend ops live on besthand-api and are reached through the `/lp-graphql`
// proxy via the `lpApi` helper (which forwards the logged-in id_token cookie).
// No payment/escrow/commission logic is duplicated here — besthand-api is the
// single source of truth. Every operation below is REUSED, unchanged:
//
//   queries:
//     - myMilestones(contractId)        -> the contract's milestone schedule
//     - projectCommissions              -> client/pro commission rates (display)
//   mutations:
//     - startMilestone(milestoneId)     [COMPANY] creates the Stripe PaymentIntent
//         on the client's card-on-file -> funds escrow, milestone -> IN_PROGRESS
//     - submitMilestoneForReview(milestoneId) [COMPANY] request release ->
//         SUBMITTED_FOR_REVIEW
//     - requestMilestoneSignatureOtp(milestoneId, documentType, party) [CLIENT]
//         6-digit SMS OTP (documentType MILESTONE_APPROVAL, party CLIENT)
//     - approveMilestone(milestoneId, otp, consentToElectronicSignature,
//         scrolledToEnd) [CLIENT] verifies OTP -> fires the Stripe Transfer to
//         the company (minus platform commission), milestone -> PAID
//
// ESCROW MODEL (per besthand-api): when the company STARTS a milestone, the
// client's card-on-file is charged and the funds are HELD by the platform
// (Stripe Connect). They are RELEASED to the company only when the client
// APPROVES the submitted milestone — payout = milestone amount minus the pro
// commission. No money moves to the company until the client approves.
//
// STATUS lifecycle: PENDING -> READY -> IN_PROGRESS (funded/in escrow) ->
// SUBMITTED_FOR_REVIEW -> APPROVED -> PAID (released). DISPUTED/CANCELLED exist
// but are surfaced read-only here.
//
// SSR-safe: no window/document/cookie access at module load or during render;
// all of it lives in lifecycle (componentDidMount) and click handlers.
// =============================================================================

// ---- GraphQL operations (against besthand-api ProjectsResponseType) ----

const Q_MILESTONES = `
  query ($contractId: String!) {
    myMilestones(contractId: $contractId) {
      status errorMessage
      milestones {
        id projectContractId orderIndex name deliverable description
        amount currency isDownPayment status
        startedAt submittedAt approvedAt paidAt createdAt
      }
    }
  }
`;

const Q_COMMISSIONS = `
  query {
    projectCommissions {
      status errorMessage
      projectCommissionClient projectCommissionPro
    }
  }
`;

const M_START = `
  mutation ($milestoneId: String!) {
    startMilestone(milestoneId: $milestoneId) {
      status errorMessage
      milestone { id status startedAt }
    }
  }
`;

const M_SUBMIT = `
  mutation ($milestoneId: String!) {
    submitMilestoneForReview(milestoneId: $milestoneId) {
      status errorMessage
      milestone { id status submittedAt }
    }
  }
`;

const M_REQUEST_OTP = `
  mutation ($milestoneId: String!, $documentType: String!, $party: String!) {
    requestMilestoneSignatureOtp(milestoneId: $milestoneId, documentType: $documentType, party: $party) {
      status errorMessage
    }
  }
`;

const M_APPROVE = `
  mutation (
    $milestoneId: String!, $otp: String!,
    $consentToElectronicSignature: Boolean!, $scrolledToEnd: Boolean!
  ) {
    approveMilestone(
      milestoneId: $milestoneId, otp: $otp,
      consentToElectronicSignature: $consentToElectronicSignature, scrolledToEnd: $scrolledToEnd
    ) {
      status errorMessage
      milestone { id status approvedAt paidAt }
    }
  }
`;

// ---- helpers ----

function money2(n, currency) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const cur = currency || 'USD';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${cur && cur !== 'USD' ? ` ${cur}` : ''}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// Client-facing status copy. Funds are held in escrow from IN_PROGRESS onward
// and released to the company at PAID.
const STATUS_LABEL = {
  PENDING: 'Pending',
  READY: 'Ready',
  IN_PROGRESS: 'In progress',
  SUBMITTED_FOR_REVIEW: 'Submitted',
  APPROVED: 'Approved',
  DISPUTED: 'Disputed',
  PAID: 'Released',
  CANCELLED: 'Cancelled',
};

// Funds are "held in escrow" once the milestone has been started/funded and
// before they are released (PAID).
const HELD_STATUSES = ['IN_PROGRESS', 'SUBMITTED_FOR_REVIEW', 'APPROVED', 'DISPUTED'];

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Reuse the exact inline path vocabulary already used across the Custom Projects
// web surfaces (MyProjects + CompanyProjects + ProjectContract ICONS) — none invented.
const ICONS = {
  wallet: 'M3 10h18M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
  shield: 'M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4Z',
  check: 'M20 6 9 17l-5-5',
  clock: 'M12 7v5l3 2 M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
  doc: 'M14 3v5h5 M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5Z',
};

class ProjectMilestones extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: '',
      milestones: [],
      commissionPro: null, // pro commission rate (fraction, e.g. 0.10)

      // per-milestone action state
      busyId: null, // milestoneId with a mutation in flight
      actError: '',

      // client approval (OTP) flow, scoped to one milestone at a time
      approveId: null, // milestoneId the client is currently approving
      consent: false,
      reviewed: false,
      otpPhase: 'idle', // 'idle' -> 'otpSent'
      otp: '',
      otpSending: false,
      approveError: '',
      approveNotice: '',
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  // eslint-disable-next-line class-methods-use-this
  firstError(res) {
    if (res && res.errors && res.errors.length) return res.errors[0].message;
    return 'Something went wrong.';
  }

  get isClient() {
    return this.props.viewerRole !== 'COMPANY';
  }

  async bootstrap() {
    const contractId = this.props.contractId;
    if (!contractId) {
      this.setState({ loading: false, error: 'No contract to load milestones for.' });
      return;
    }
    this.setState({ loading: true, error: '' });
    try {
      const [mRes, cRes] = await Promise.all([
        lpApi(Q_MILESTONES, { contractId: String(contractId) }),
        lpApi(Q_COMMISSIONS),
      ]);
      const mEnv = mRes && mRes.data && mRes.data.myMilestones;
      if (mRes.errors || !mEnv || (mEnv.status && mEnv.status >= 400)) {
        this.setState({ loading: false, error: (mEnv && mEnv.errorMessage) || this.firstError(mRes) });
        return;
      }
      const cEnv = cRes && cRes.data && cRes.data.projectCommissions;
      const commissionPro = (cEnv && cEnv.status === 200 && cEnv.projectCommissionPro != null)
        ? Number(cEnv.projectCommissionPro)
        : null;
      this.setState({
        loading: false,
        milestones: (mEnv.milestones || []).slice().sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)),
        commissionPro,
      });
    } catch (e) {
      this.setState({ loading: false, error: `Could not load milestones: ${e.message}` });
    }
  }

  // Replace one milestone in state from a mutation response (keeps order).
  patchMilestone(updated) {
    if (!updated || updated.id == null) return;
    this.setState((st) => ({
      milestones: st.milestones.map((m) => (String(m.id) === String(updated.id) ? { ...m, ...updated } : m)),
    }));
  }

  // ---- company actions: start (fund escrow) + submit (request release) ----

  async startMilestone(id) {
    this.setState({ busyId: id, actError: '' });
    try {
      const res = await lpApi(M_START, { milestoneId: String(id) });
      const env = res && res.data && res.data.startMilestone;
      if (res.errors || !env || (env.status && env.status >= 400) || !env.milestone) {
        this.setState({ busyId: null, actError: (env && env.errorMessage) || this.firstError(res) });
        return;
      }
      this.patchMilestone(env.milestone);
      this.setState({ busyId: null });
    } catch (e) {
      this.setState({ busyId: null, actError: `Could not start the milestone: ${e.message}` });
    }
  }

  async submitMilestone(id) {
    this.setState({ busyId: id, actError: '' });
    try {
      const res = await lpApi(M_SUBMIT, { milestoneId: String(id) });
      const env = res && res.data && res.data.submitMilestoneForReview;
      if (res.errors || !env || (env.status && env.status >= 400) || !env.milestone) {
        this.setState({ busyId: null, actError: (env && env.errorMessage) || this.firstError(res) });
        return;
      }
      this.patchMilestone(env.milestone);
      this.setState({ busyId: null });
    } catch (e) {
      this.setState({ busyId: null, actError: `Could not submit the milestone: ${e.message}` });
    }
  }

  // ---- client action: approve a submitted milestone -> release escrow ----

  openApprove(id) {
    this.setState({
      approveId: id,
      consent: false,
      reviewed: false,
      otpPhase: 'idle',
      otp: '',
      approveError: '',
      approveNotice: '',
    });
  }

  closeApprove() {
    this.setState({ approveId: null, otpPhase: 'idle', otp: '', approveError: '', approveNotice: '' });
  }

  canSendApprovalOtp() {
    return this.state.consent && this.state.reviewed && !this.state.otpSending;
  }

  async sendApprovalOtp() {
    if (!this.canSendApprovalOtp()) return;
    const id = this.state.approveId;
    this.setState({ otpSending: true, approveError: '', approveNotice: '' });
    try {
      const res = await lpApi(M_REQUEST_OTP, {
        milestoneId: String(id),
        documentType: 'MILESTONE_APPROVAL',
        party: 'CLIENT',
      });
      const env = res && res.data && res.data.requestMilestoneSignatureOtp;
      if (res.errors || !env || env.status !== 200) {
        this.setState({ otpSending: false, approveError: (env && env.errorMessage) || this.firstError(res) });
        return;
      }
      this.setState({
        otpSending: false,
        otpPhase: 'otpSent',
        approveNotice: 'We texted a 6-digit approval code to the phone on file.',
      });
    } catch (e) {
      this.setState({ otpSending: false, approveError: `Could not send the code: ${e.message}` });
    }
  }

  async confirmApprove() {
    const { approveId, consent, reviewed, otp } = this.state;
    if (!consent || !reviewed) {
      this.setState({ approveError: 'Please confirm the approval items before releasing payment.' });
      return;
    }
    if ((otp || '').replace(/\D/g, '').length < 4) {
      this.setState({ approveError: 'Enter the verification code we texted you.' });
      return;
    }
    this.setState({ busyId: approveId, approveError: '', approveNotice: '' });
    try {
      const res = await lpApi(M_APPROVE, {
        milestoneId: String(approveId),
        otp: String(otp).trim(),
        consentToElectronicSignature: true, // ESIGN consent
        scrolledToEnd: true, // intent — gated client-side by `reviewed`
      });
      const env = res && res.data && res.data.approveMilestone;
      if (res.errors || !env || (env.status && env.status >= 400) || !env.milestone) {
        this.setState({ busyId: null, approveError: (env && env.errorMessage) || this.firstError(res) });
        return;
      }
      this.patchMilestone(env.milestone);
      this.setState({
        busyId: null,
        approveId: null,
        otpPhase: 'idle',
        otp: '',
      });
    } catch (e) {
      this.setState({ busyId: null, approveError: `We couldn’t release the payment: ${e.message}` });
    }
  }

  // ---- derived: escrow + commission summary ----

  escrowSummary() {
    const { milestones, commissionPro } = this.state;
    let held = 0; // funds currently held in escrow (funded, not yet released)
    let released = 0; // funds already released to the company
    let total = 0;
    milestones.forEach((m) => {
      const amt = Number(m.amount) || 0;
      total += amt;
      if (m.status === 'PAID') released += amt;
      else if (HELD_STATUSES.includes(m.status)) held += amt;
    });
    const proRate = commissionPro != null ? commissionPro : 0.10;
    return {
      held,
      released,
      total,
      proRate,
      releasedFee: Math.round(released * proRate * 100) / 100,
    };
  }

  // ---- render ----

  render() {
    const { loading, error } = this.state;
    if (loading) return <div className={s.loading}>Loading milestones…</div>;
    if (error) return <div className={s.alertErr}>{error}</div>;
    return (
      <div className={s.wrap}>
        {this.renderEscrowCard()}
        {this.renderSchedule()}
      </div>
    );
  }

  renderEscrowCard() {
    const { milestones } = this.state;
    if (!milestones.length) return null;
    const sum = this.escrowSummary();
    const currency = (milestones[0] && milestones[0].currency) || 'USD';
    return (
      <div className={s.escrowCard}>
        <p className={s.escrowTitle}><Icon d={ICONS.shield} /> Escrow protection</p>
        <div className={s.escrowGrid}>
          <div className={s.escrowStat}>
            <span className={s.escrowNum}>{money2(sum.held, currency)}</span>
            <span className={s.escrowLabel}>Held in escrow</span>
          </div>
          <div className={s.escrowStat}>
            <span className={`${s.escrowNum} ${s.escrowReleased}`}>{money2(sum.released, currency)}</span>
            <span className={s.escrowLabel}>Released to company</span>
          </div>
          <div className={s.escrowStat}>
            <span className={s.escrowNum}>{money2(sum.total, currency)}</span>
            <span className={s.escrowLabel}>Project total</span>
          </div>
        </div>
        <div className={s.escrowBreak}>
          <span className={s.escrowBreakRow}>
            Platform commission on released payouts ({Math.round(sum.proRate * 100)}%)
            <strong>{money2(sum.releasedFee, currency)}</strong>
          </span>
        </div>
        <p className={s.escrowNote}>
          {this.isClient
            ? 'Your payments are held by BestHand and released to the company milestone by milestone — only after you approve the work. No money moves until you approve.'
            : 'Client funds are held in escrow by BestHand. Each milestone is released to your Stripe account once the client approves it, minus the platform commission.'}
        </p>
      </div>
    );
  }

  renderSchedule() {
    const { milestones, actError } = this.state;
    if (!milestones.length) {
      return (
        <div className={s.card}>
          <p className={s.cardTitle}><Icon d={ICONS.wallet} /> Milestone schedule</p>
          <p className={s.emptyText}>
            The milestone schedule will appear here once the contract is active and escrow opens.
          </p>
        </div>
      );
    }
    return (
      <div className={s.card}>
        <p className={s.cardTitle}><Icon d={ICONS.wallet} /> Milestone schedule</p>
        {actError ? <div className={s.alertErr}>{actError}</div> : null}
        <div className={s.msList}>
          {milestones.map((m, idx) => this.renderRow(m, idx))}
        </div>
      </div>
    );
  }

  renderRow(m, idx) {
    const currency = m.currency || 'USD';
    const statusLabel = STATUS_LABEL[m.status] || m.status;
    const statusCls = s[`st_${(m.status || '').toLowerCase()}`] || '';
    const isApproving = String(this.state.approveId) === String(m.id);
    return (
      <div className={`${s.msRow} ${isApproving ? s.msRowOpen : ''}`} key={m.id}>
        <div className={s.msMain}>
          <span className={s.msIndex}>{idx + 1}</span>
          <div className={s.msInfo}>
            <span className={s.msName}>
              {m.name || `Milestone ${idx + 1}`}
              {m.isDownPayment ? <span className={s.msTag}>Deposit</span> : null}
            </span>
            {m.description ? <span className={s.msDesc}>{m.description}</span> : null}
            {this.renderTimeline(m)}
          </div>
          <div className={s.msRight}>
            <span className={s.msAmt}>{money2(m.amount, currency)}</span>
            <span className={`${s.statusPill} ${statusCls}`}>{statusLabel}</span>
          </div>
        </div>
        {this.renderActions(m)}
        {isApproving ? this.renderApprovePanel(m) : null}
      </div>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  renderTimeline(m) {
    const pts = [];
    if (m.startedAt) pts.push(`Funded ${fmtDateTime(m.startedAt)}`);
    if (m.submittedAt) pts.push(`Submitted ${fmtDateTime(m.submittedAt)}`);
    if (m.paidAt) pts.push(`Released ${fmtDateTime(m.paidAt)}`);
    if (!pts.length) return null;
    return <span className={s.msTimeline}>{pts.join(' · ')}</span>;
  }

  renderActions(m) {
    const { busyId } = this.state;
    const busy = String(busyId) === String(m.id);

    if (this.isClient) {
      // Client may APPROVE (release) a SUBMITTED milestone.
      if (m.status === 'SUBMITTED_FOR_REVIEW') {
        if (String(this.state.approveId) === String(m.id)) return null; // panel open below
        return (
          <div className={s.msActions}>
            <button type="button" className={s.btn} disabled={busy} onClick={() => this.openApprove(m.id)}>
              Review &amp; release payment
            </button>
          </div>
        );
      }
      if (m.status === 'IN_PROGRESS') {
        return <p className={s.msHint}><Icon d={ICONS.clock} /> Funded &amp; held in escrow — the company is working on this milestone.</p>;
      }
      if (m.status === 'PAID') {
        return <p className={s.msHintDone}><Icon d={ICONS.check} /> Approved &amp; released to the company.</p>;
      }
      return null;
    }

    // Company side: start (fund escrow) a READY milestone, then submit it.
    if (m.status === 'READY') {
      return (
        <div className={s.msActions}>
          <button type="button" className={s.btn} disabled={busy} onClick={() => this.startMilestone(m.id)}>
            {busy ? 'Starting…' : 'Start milestone (fund escrow)'}
          </button>
        </div>
      );
    }
    if (m.status === 'IN_PROGRESS') {
      return (
        <div className={s.msActions}>
          <button type="button" className={s.btn} disabled={busy} onClick={() => this.submitMilestone(m.id)}>
            {busy ? 'Submitting…' : 'Submit for client approval'}
          </button>
        </div>
      );
    }
    if (m.status === 'SUBMITTED_FOR_REVIEW') {
      return <p className={s.msHint}><Icon d={ICONS.clock} /> Awaiting client approval to release your payout.</p>;
    }
    if (m.status === 'PAID') {
      return <p className={s.msHintDone}><Icon d={ICONS.check} /> Paid out — released from escrow to your account.</p>;
    }
    if (m.status === 'PENDING') {
      return <p className={s.msHint}>Becomes available once the previous milestone is released.</p>;
    }
    return null;
  }

  renderApprovePanel(m) {
    const {
      consent, reviewed, otpPhase, otp, otpSending, busyId, approveError, approveNotice,
    } = this.state;
    const currency = m.currency || 'USD';
    const sum = this.escrowSummary();
    const fee = Math.round((Number(m.amount) || 0) * sum.proRate * 100) / 100;
    const payout = Math.round(((Number(m.amount) || 0) - fee) * 100) / 100;
    const busy = String(busyId) === String(m.id);

    return (
      <div className={s.approveBox}>
        <p className={s.approveTitle}><Icon d={ICONS.doc} /> Approve &amp; release this milestone</p>
        <p className={s.approveBody}>
          Approving releases <strong>{money2(m.amount, currency)}</strong> from escrow to the company
          (their payout is {money2(payout, currency)} after the {Math.round(sum.proRate * 100)}% platform
          commission of {money2(fee, currency)}). This action is final.
        </p>

        {approveError ? <div className={s.alertErr}>{approveError}</div> : null}
        {approveNotice ? <div className={s.alertNotice}>{approveNotice}</div> : null}

        <label className={s.consentRow}>
          <input type="checkbox" checked={reviewed}
            onChange={(e) => this.setState({ reviewed: e.target.checked })} />
          <span className={s.consentText}>
            I have reviewed the completed work for this milestone and approve releasing the escrowed payment.
          </span>
        </label>
        <label className={s.consentRow}>
          <input type="checkbox" checked={consent}
            onChange={(e) => this.setState({ consent: e.target.checked })} />
          <span className={s.consentText}>
            I consent to confirm this approval electronically (ESIGN/UETA) and agree it is legally binding.
          </span>
        </label>

        {otpPhase === 'idle' ? (
          <div className={s.approveActions}>
            <button type="button" className={s.btnGhost} disabled={busy || otpSending}
              onClick={() => this.closeApprove()}>
              Cancel
            </button>
            <button type="button" className={s.btn} disabled={!this.canSendApprovalOtp()}
              onClick={() => this.sendApprovalOtp()}>
              {otpSending ? 'Sending code…' : 'Send approval code'}
            </button>
          </div>
        ) : (
          <div>
            <div className={s.otpRow}>
              <div>
                <span className={s.fieldLabel}>Verification code</span>
                <input className={`${s.input} ${s.codeInput}`} type="text" inputMode="numeric" maxLength={6}
                  value={otp} placeholder="6-digit code"
                  onChange={(e) => this.setState({ otp: e.target.value.replace(/\D/g, '').slice(0, 6) })} />
              </div>
              <button type="button" className={s.btn} disabled={busy || (otp || '').length < 4}
                onClick={() => this.confirmApprove()}>
                {busy ? 'Releasing…' : 'Approve & release'}
              </button>
            </div>
            <div className={s.approveFootRow}>
              <button type="button" className={s.linkBtn} disabled={otpSending}
                onClick={() => this.sendApprovalOtp()}>
                Resend code
              </button>
              <button type="button" className={s.linkBtn} disabled={busy}
                onClick={() => this.closeApprove()}>
                Cancel
              </button>
            </div>
          </div>
        )}
        <p className={s.scrollNote}>
          We’ll text a 6-digit code to the mobile number on your account to confirm it’s really you before
          releasing escrowed funds.
        </p>
      </div>
    );
  }
}

export default withStyles(s)(ProjectMilestones);
